/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  Object Strucutre:
                    path => router path
                    name => router name
                    component(lazy loading) => component to load
                    meta : {
                      rule => which user can have access (ACL)
                      breadcrumb => Add breadcrumb to specific page
                      pageTitle => Display title besides breadcrumb
                    }
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/

import Vue from "vue";
import Router from "vue-router";
import auth from "@/auth/authService";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";

Vue.use(Router);

const router = new Router({
  mode: "history",
  base: "/",
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
  routes: [
    {
      //=============================================================================
      // MAIN LAYOUT ROUTES
      //=============================================================================
      path: "",
      component: () => import("./layouts/main/Main.vue"),
      children: [
        //=============================================================================
        // Theme Routes
        //=============================================================================
        {
          path: "/operations/manage-educational-consultants",
          name: "educational-consultants",
          component: () => import("@/views/apps/operations/Operation.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Educational consultants" },
              { title: "List", active: true },
            ],
            pageTitle: "Educational consultants",
            rule: "UserRoles",
            role: ["admin"],
            permission: "educational-consultant.index",
          },
        },
        {
          path: "/operations/manage-educational-consultant-team",
          name: "manage-educational-consultant-team",
          component: () => import("@/views/apps/operations/Operation.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Educational consultant Teams" },
              { title: "List", active: true },
            ],
            pageTitle: "Educational consultant Teams",
            rule: "UserRoles",
            role: ["admin"],
            permission: "educational-consultant-team.index",
          },
        },
        {
          path: "/operations/manage-educational-consultant-in-charge-of-user",
          name: "manage-educational-consultant-in-charge-of-user",
          component: () => import("@/views/apps/operations/Operation.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "In charge of User" },
              { title: "List", active: true },
            ],
            pageTitle: "In charge of User",
            rule: "UserRoles",
            role: ["admin"],
            permission: "educational-consultant-in-charge-of-user.index",
          },
        },
        {
          path: "/operations/manage-customer-success",
          name: "manage-customer-success",
          component: () => import("@/views/apps/operations/Operation.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Customer Success" },
              { title: "List", active: true },
            ],
            pageTitle: "Customer Success",
            rule: "UserRoles",
            role: ["admin"],
            permission: "service-officer.index",
          },
        },
        {
          path: "/operations/manage-customer-success-teams",
          name: "manage-customer-success-teams",
          component: () => import("@/views/apps/operations/Operation.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Customer Success Teams" },
              { title: "List", active: true },
            ],
            pageTitle: "Customer Success Teams",
            rule: "UserRoles",
            role: ["admin"],
            permission: "service-officer-team.index",
          },
        },
        {
          path: "/operations/manage-customer-success-characters",
          name: "manage-customer-success-characters",
          component: () => import("@/views/apps/operations/Operation.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Customer Success Characters" },
              { title: "List", active: true },
            ],
            pageTitle: "Customer Success Characters",
            rule: "UserRoles",
            role: ["admin"],
            permission: "service-officer-character.index",
          },
        },

        {
          path: "/operations/manage-student-contacts",
          name: "manage-student-contacts",
          component: () =>
            import("@/views/apps/student/pages/StudentContactManager.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Contacts" },
              { title: "List", active: true },
            ],
            pageTitle: "Contacts",
            rule: "UserRoles",
            role: ["admin"],
            permission: "student-contact.index",
          },
        },
        {
          path: "/operations/report-sale",
          name: "report-sale",
          component: () => import("@/views/apps/operations/Operation.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Report sale" },
              { title: "List", active: true },
            ],
            pageTitle: "Report sale",
            rule: "UserRoles",
            role: ["admin"],
            permission: "report-ec-sale.index",
          },
        },
        {
          path: "/",
          redirect: "/dashboard",
          meta: {
            rule: "UserRoles",
            role: ["*"],
            permission: "dashboard.index",
          },
        },
        {
          path: "/dashboard",
          name: "dashboard-analytics",
          component: () => import("./views/DashboardAnalytics.vue"),
          meta: {
            rule: "UserRoles",
            role: ["admin"],
            permission: "dashboard.index",
          },
        },

        //=============================================================================
        // Chat Routes
        //=============================================================================
        {
          path: "/chats",
          name: "list-chat",
          component: () => import("@/views/apps/chat/ChatList.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Chat List", active: true },
            ],
            pageTitle: "List Chat",
            rule: "UserRoles",
            role: ["admin"],
            permission: "tools.chat.index",
          },
        },

        //=============================================================================
        // Tickets
        //=============================================================================
        {
          path: "/tickets",
          name: "list-ticket",
          component: () => import("@/views/apps/ticket/TicketList.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Chat ticket", active: true },
            ],
            pageTitle: "List Ticket",
            rule: "UserRoles",
            role: ["admin"],
            permission: "tickets.index",
          },
        },

        //=============================================================================
        // Learning Libraries
        //=============================================================================
        {
          path: "/learning-libraries",
          name: "learning-libraries",
          component: () =>
            import("@/views/apps/libraries/LearningLibraryManager.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Learning Libraries", active: true },
            ],
            pageTitle: "Learning Libraries",
            rule: "UserRoles",
            role: ["admin"],
            permission: "communication-topics.index",
          },
        },

        //=============================================================================
        // Application Routes
        //=============================================================================
        {
          path: "/teacher/my-profile",
          name: "app-my-profile-teacher-view",
          component: () =>
            import(
              "@/views/apps/teacher/teacher-view/MyProfileTeacherView.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "View", active: true },
            ],
            pageTitle: "Teacher View",
            rule: "UserRoles",
            role: ["admin", "teacher"],
            permission: null,
          },
        },
        {
          path: "/users",
          name: "app-user-list",
          component: () => import("@/views/apps/user/user-list/UserList.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "User" },
              { title: "List", active: true },
            ],
            pageTitle: "User List",
            rule: "UserRoles",
            role: ["admin"],
            permission: null,
          },
        },
        {
          path: "/user/add",
          name: "app-user-new",
          component: () => import("@/views/apps/user/user-edit/UserEdit.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "User" },
              { title: "Add", active: true },
            ],
            pageTitle: "Create User",
            rule: "UserRoles",
            role: ["admin"],
            permission: null,
          },
        },
        {
          path: "/user/:userId",
          name: "app-user-view",
          component: () => import("@/views/apps/user/UserView.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "User" },
              { title: "View", active: true },
            ],
            pageTitle: "User View",
            rule: "UserRoles",
            role: ["admin"],
            permission: null,
          },
        },
        {
          path: "/user/edit/:userId",
          name: "app-user-edit",
          component: () => import("@/views/apps/user/user-edit/UserEdit.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "User" },
              { title: "Edit", active: true },
            ],
            pageTitle: "User Edit",
            rule: "UserRoles",
            role: ["admin"],
            permission: null,
          },
        },
        {
          path: "/apps/role/role-list",
          name: "app-event-role-list",
          component: () => import("@/views/apps/role/Role.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Roles" },
              { title: "List", active: true },
            ],
            pageTitle: "Role List",
            rule: "UserRoles",
            role: ["admin"],
            permission: null,
          },
        },
        {
          path: "/students",
          name: "app-student-list",
          component: () => import("@/views/apps/student/pages/StudentManager"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Student", url: "/students" },
              { title: "List", active: true },
            ],
            pageTitle: "Student List",
            rule: "UserRoles",
            role: ["admin"],
            permission: "students.index",
          },
        },
        {
          path: "/students/:studentId",
          name: "app-student-view",
          component: () =>
            import("@/views/apps/student/pages/StudentDetail.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Student", url: "/students" },
              { title: "View", active: true },
            ],
            pageTitle: "Student View",
            rule: "UserRoles",
            role: ["admin"],
            permission: "students.show",
          },
        },

        //=============================================================================
        // Meetings
        //=============================================================================
        {
          path: "/meetings",
          name: "app-meetings-list",
          component: () =>
            import("@/views/apps/meeting/meetings-list/MeetingList.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Meetings", url: "/meetings" },
              { title: "List", active: true },
            ],
            pageTitle: "Meeting List",
            rule: "UserRoles",
            role: ["admin"],
            permission: "student-meetings.index",
          },
        },

        //=============================================================================
        // Emails
        //=============================================================================
        {
          path: "/emails",
          name: "app-template-email-list",
          component: () =>
            import(
              "@/views/apps/template-email/template-email-list/TemplateEmailList.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Emails", url: "/emails" },
              { title: "List", active: true },
            ],
            pageTitle: "Email List",
            rule: "UserRoles",
            role: ["admin"],
            permission: "email-templates.index",
          },
        },
        {
          path: "/email/add",
          name: "app-email-new",
          component: () =>
            import("@/views/apps/template-email/add-template/TemplateAdd.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Emails", url: "/emails" },
              { title: "Add", active: true },
            ],
            pageTitle: "Add Template",
            rule: "UserRoles",
            role: ["admin"],
            permission: "email-templates.add",
          },
        },
        {
          path: "/email/:id",
          name: "app-email-view",
          component: () =>
            import("@/views/apps/template-email/add-template/TemplateAdd.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Emails", url: "/emails" },
              { title: "Edit", active: true },
            ],
            pageTitle: "Edit Template",
            rule: "UserRoles",
            role: ["admin"],
            permission: "email-templates.show",
          },
        },

        //=============================================================================
        // Workflow
        //=============================================================================
        {
          path: "/workflows",
          name: "app-workflow-list",
          component: () =>
            import("@/views/apps/workflow/workflow-list/WorkflowList.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Workflows", url: "/workflows" },
              { title: "List", active: true },
            ],
            pageTitle: "Workflow List",
            rule: "UserRoles",
            role: ["admin"],
            permission: "email-list-students.index",
          },
        },
        {
          path: "/workflows/add",
          name: "app-workflow-new",
          component: () =>
            import("@/views/apps/workflow/workflow-view/WorkflowView.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Workflows", url: "/workflows" },
              { title: "Add", active: true },
            ],
            pageTitle: "Add Workflow",
            rule: "UserRoles",
            role: ["admin"],
            permission: "email-list-students.add",
          },
        },
        {
          path: "/workflows/:id",
          name: "app-email-view",
          component: () =>
            import("@/views/apps/workflow/workflow-view/WorkflowView.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Workflows", url: "/workflows" },
              { title: "Add", active: true },
            ],
            pageTitle: "Edit Workflow",
            rule: "UserRoles",
            role: ["admin"],
            permission: "email-list-students.show",
          },
        },

        //=============================================================================
        // Teacher
        //=============================================================================
        {
          path: "/teachers",
          name: "app-teacher-list",
          component: () => import("@/views/apps/teacher/TeacherList.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Teacher", url: "/teachers" },
              { title: "List", active: true },
            ],
            pageTitle: "Teacher List",
            rule: "UserRoles",
            role: ["admin"],
            permission: "teachers.index",
          },
        },
        {
          path: "/teachers/:teacherId",
          name: "app-teacher-view",
          component: () =>
            import("@/views/apps/teacher/teacher-view/TeacherView.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Teacher", url: "/teachers" },
              { title: "View", active: true },
            ],
            pageTitle: "Teacher View",
            rule: "UserRoles",
            role: ["admin"],
            permission: "teachers.show",
          },
        },

        //=============================================================================
        // Course
        //=============================================================================
        {
          path: "/courses",
          name: "app-course-list",
          component: () => import("@/views/apps/course/CourseList.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Course", url: "/courses" },
              { title: "List", active: true },
            ],
            pageTitle: "Course List",
            rule: "UserRoles",
            role: ["admin"],
            permission: "courses.index",
          },
        },
        {
          path: "/courses/:courseId",
          name: "app-course-view",
          component: () =>
            import("@/views/apps/course/course-view/CourseView.vue"),
          meta: {
            // breadcrumb: [
            //   { title: 'Home', url: '/' },
            //   { title: 'Course', url: '/courses' },
            //   { title: 'View', active: true }
            // ],
            // pageTitle: 'Course View',

            rule: "UserRoles",
            role: ["admin"],
            permission: "courses.show",
          },
        },

        //=============================================================================
        // Vouchers
        //=============================================================================
        {
          path: "/vouchers",
          name: "app-voucher-list",
          component: () => import("@/views/apps/voucher/VoucherList.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Vouchers", url: "/vouchers" },
              { title: "List", active: true },
            ],
            pageTitle: "Voucher List",
            rule: "UserRoles",
            role: ["admin"],
            permission: "vouchers.index",
          },
        },

        //=============================================================================
        // Combos
        //=============================================================================
        {
          path: "/combos",
          name: "app-combo-list",
          component: () => import("@/views/apps/combo/ComboList.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Combo", url: "/combos" },
              { title: "List", active: true },
            ],
            pageTitle: "Combo List",
            rule: "UserRoles",
            role: ["admin"],
            permission: "combo.index",
          },
        },
        {
          path: "/combos/:comboId",
          name: "app-combos-view",
          component: () => import("@/views/apps/combo/ComboView.vue"),
          meta: {
            rule: "UserRoles",
            role: ["admin"],
            permission: "combo.show",
          },
        },

        //=============================================================================
        // Schedules
        //=============================================================================
        {
          path: "/schedules",
          name: "app-schedule-list",
          component: () => import("@/views/apps/schedule/pages/ScheduleManage.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Schedule", url: "/schedules" },
              { title: "List", active: true },
            ],
            pageTitle: "Schedule List",
            rule: "UserRoles",
            role: ["admin"],
            permission: "schedules.course-timetables.index",
          },
        },
        {
          path: "/schedules/auto-creator",
          name: "auto-schedule-creator",
          component: () => import("@/views/apps/schedule/pages/AutoScheduleCreator.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Schedule", url: "/schedules" },
              { title: "Tạo lịch tự động", active: true },
            ],
            pageTitle: "Tạo lịch tự động",
            rule: "UserRoles",
            role: ["admin"],
            permission: "schedules.course-timetables.add",
          },
        },

        //=============================================================================
        // Branches
        //=============================================================================
        {
          path: "/branches",
          name: "app-branch-list",
          component: () => import("@/views/apps/branch/BranchList.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Branch", url: "/branches" },
              { title: "List", active: true },
            ],
            pageTitle: "Branch List",
            rule: "UserRoles",
            role: ["admin"],
            permission: "branches.index",
          },
        },

        //=============================================================================
        // Levels
        //=============================================================================
        {
          path: "/levels",
          name: "app-level-list",
          component: () => import("@/views/apps/level/LevelList.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Level", url: "/levels" },
              { title: "List", active: true },
            ],
            pageTitle: "Level List",
            rule: "UserRoles",
            role: ["admin"],
            permission: "levels.index",
          },
        },

        //=============================================================================
        // Lessons
        //=============================================================================
        {
          path: "/curriculums",
          name: "app-curriculum-list",
          component: () =>
            import("@/views/apps/curriculum/CurriculumManager.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Curriculum" },
              { title: "List", active: true },
            ],
            pageTitle: "Curriculum List",
            rule: "UserRoles",
            role: ["admin", "teacher"],
            permission: "curriculums.index",
          },
        },

        //=============================================================================
        // Shifts
        //=============================================================================
        {
          path: "/shifts",
          name: "app-shift-list",
          component: () => import("@/views/apps/shift/ShiftList.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Shift" },
              { title: "List", active: true },
            ],
            pageTitle: "Shift List",
            rule: "UserRoles",
            role: ["admin"],
            permission: "shifts.index",
          },
        },

        //=============================================================================
        // Certificates
        //=============================================================================
        {
          path: "/certificates",
          name: "app-certificate-list",
          component: () =>
            import("@/views/apps/certificate/CertificateList.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Certificate" },
              { title: "List", active: true },
            ],
            pageTitle: "Certificate List",
            rule: "UserRoles",
            role: ["admin"],
            permission: "certificates.index",
          },
        },

        //=============================================================================
        // Freezings
        //=============================================================================
        {
          path: "/freezings",
          name: "app-freezing-list",
          component: () => import("@/views/apps/freezing/FreezingList.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "ET Freezing" },
              { title: "List", active: true },
            ],
            pageTitle: "ET Freezing List",
            rule: "UserRoles",
            role: ["admin"],
            permission: "freezings.index",
          },
        },

        //=============================================================================
        // Holidays
        //=============================================================================
        {
          path: "/holidays",
          name: "app-holiday-list",
          component: () => import("@/views/apps/holiday/HolidayList.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Holiday" },
              { title: "List", active: true },
            ],
            pageTitle: "Holiday List",
            rule: "UserRoles",
            role: ["admin"],
            permission: "holidays.index",
          },
        },

        //=============================================================================
        // Test forms
        //=============================================================================
        {
          path: "/test-forms",
          name: "app-test-form-list",
          component: () => import("@/views/apps/test-form/TestFormList.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Test Form", url: "/test-forms" },
              { title: "List", active: true },
            ],
            pageTitle: "Test Form List",
            rule: "UserRoles",
            role: ["admin"],
            permission: "template.test-forms.index",
          },
        },
        //=============================================================================
        // Call Logs
        //=============================================================================
        {
          path: "/call/logs",
          name: "app-stringee-call-log-list",
          component: () => import("@/views/apps/call/CallLog.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Call Logs", url: "/call/logs" },
              { title: "List", active: true },
            ],
            pageTitle: "Call logs",
            rule: "UserRoles",
            role: ["admin"],
            permission: "stringee-call-log.index",
          },
        },
        //=============================================================================
        // App Notification
        //=============================================================================
        {
          path: "/app-notifications",
          name: "app-stringee-call-log-list",
          component: () =>
            import("@/views/apps/notifications/AppNotification.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Call Logs", url: "/call/logs" },
              { title: "List", active: true },
            ],
            pageTitle: "App Notifications",
            rule: "UserRoles",
            role: ["admin"],
            //permission: '',
          },
        },

        //=============================================================================
        // LMS
        //=============================================================================
        {
          path: "/curriculums/categories",
          name: "Curriculum Categories",
          component: () => import("@/views/apps/library-category/Category.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Curriculums" },
              { title: "Curriculum Categories", active: true },
            ],
            pageTitle: "Curriculum Categories",
            rule: "UserRoles",
            role: ["admin"],
            permission: "curriculum-categories.index",
          },
        },
        {
          path: "/curriculums/levels",
          name: "Curriculum Levels",
          component: () => import("@/views/apps/lms-level/ListLevel.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Curriculums" },
              { title: "Levels", active: true },
            ],
            pageTitle: "Level",
            rule: "UserRoles",
            role: ["admin"],
            permission: "curriculum-levels.index",
          },
        },
        {
          path: "/curriculums/courses",
          name: "curriculum-courses",
          component: () =>
            import("@/views/apps/curriculum-course/ListCourse.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Curriculums" },
              { title: "Curriculum Courses", active: true },
            ],
            pageTitle: "Curriculum Courses",
            rule: "UserRoles",
            role: ["admin"],
            permission: "curriculum-courses.index",
          },
        },
        {
          path: "/curriculums/courses/trashed",
          name: "curriculum-courses",
          component: () =>
            import("@/views/apps/curriculum-course/ListCourseTrashed.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Curriculum Courses", url: "/curriculums/courses" },
              { title: "Trashed", active: true },
            ],
            pageTitle: "Curriculum Courses Trashed",
            rule: "UserRoles",
            role: ["admin"],
            permission: "curriculum-courses.index",
          },
        },
        {
          path: "/curriculums/courses/:id",
          name: "curriculum-course-detail",
          component: () =>
            import("@/views/apps/curriculum-course/CourseDetail.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Curriculum Courses", url: "/curriculums/courses" },
              { title: "Detail", active: true },
            ],
            pageTitle: "",
            rule: "UserRoles",
            role: ["admin"],
            permission: "curriculum-courses.detail",
          },
        },
        {
          path: "/curriculums/self-study-courses/:id",
          name: "curriculum-self-study-course-detail",
          component: () =>
            import("@/views/apps/curriculum-course/CustomizeCourseDetail.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Students", url: "/students" },
              { title: "Self-Study Course", active: true },
            ],
            pageTitle: "",
            rule: "UserRoles",
            role: ["admin"],
            permission: "curriculum-courses.detail",
          },
        },
        {
          path: "/flipbook/:id",
          name: "flipbook",
          component: () => import("@/views/apps/flipbook/Flipbook.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "FlipBook", url: "/flipbook", active: true },
            ],
            pageTitle: "FlipBook",
            rule: "UserRoles",
            role: ["admin"],
            permission: "*",
          },
        },
        {
          path: "/prices",
          name: "Prices",
          component: () => import("@/views/apps/package/PackageManager.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Price", active: true },
            ],
            pageTitle: "Price",
            rule: "UserRoles",
            role: ["admin"],
            permission: "price.index",
          },
        },
        {
          path: "/medias",
          name: "Medias",
          component: () => import("@/views/apps/media/VideoList.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Media", active: true },
            ],
            pageTitle: "Media",
            rule: "UserRoles",
            role: ["admin"],
            permission: "media.index",
          },
        },
        {
          path: "/requirements",
          name: "Requirements",
          component: () => import("@/views/apps/media/RequirementManager.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Requirements", active: true },
            ],
            pageTitle: "Requirements",
            rule: "UserRoles",
            role: ["admin"],
            permission: "requirements.index",
          },
        },
        //=============================================================================
        // Q & A
        //=============================================================================
        {
          path: "/qna",
          name: "question-and-answer",
          component: () => import("@/views/apps/question-and-answer/QA.vue"),
          meta: {
            pageTitle: "Question and answer",
            rule: "UserRoles",
            role: ["admin"],
            permission: "*",
          },
        },

        //=============================================================================
        // CMS
        //=============================================================================
        {
          path: "/pages",
          name: "pages",
          component: () => import("@/views/apps/cms/pages/PageList.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Pages", active: true },
            ],
            pageTitle: "Pages",
            rule: "UserRoles",
            role: ["admin"],
            permission: "pages.index",
          },
        },
        {
          path: "/pages/edit/:id",
          component: () =>
            import("@/views/apps/cms/pages/component/PageEdit.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Pages", url: "/pages" },
              { title: "Edit", active: true },
            ],
            pageTitle: "Pages",
            rule: "UserRoles",
            role: ["admin"],
            permission: "pages.update",
          },
        },
        {
          path: "/menu",
          name: "menu",
          // component: () => import("@/views/apps/cms/menu/MenuList.vue"),
          component: () => import("@/views/apps/cms/menu/Menu.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Menu", active: true },
            ],
            pageTitle: "Menu",
            rule: "UserRoles",
            role: ["admin"],
            permission: "menu.index",
          },
        },
        {
          path: "/blog",
          name: "blog",
          component: () => import("@/views/apps/cms/library/LibraryList.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "BLog", active: true },
            ],
            pageTitle: "BLog",
            rule: "UserRoles",
            role: ["admin"],
            permission: "blog.index",
          },
        },
        {
          path: "/blog/:id/grammar",
          component: () =>
            import("@/views/apps/cms/library-grammar/LibraryGrammarList.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "BLog", url: "/blog" },
              { title: "Grammar", active: true },
            ],
            pageTitle: "Grammar",
            rule: "UserRoles",
            role: ["admin"],
            permission: "blog.grammar.index",
          },
        },
        {
          path: "/forms",
          component: () => import("@/views/apps/cms/form/FormList.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Forms", active: true },
            ],
            pageTitle: "Forms",
            rule: "UserRoles",
            role: ["admin"],
            permission: "forms.index",
          },
        },
        {
          path: "/forms/add",
          component: () =>
            import("@/views/apps/cms/form/component/FormEdit.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Forms", url: "/forms" },
              { title: "Add", active: true },
            ],
            pageTitle: "Forms",
            rule: "UserRoles",
            role: ["admin"],
            permission: "forms.add",
          },
        },
        {
          path: "/form/edit/:id",
          component: () =>
            import("@/views/apps/cms/form/component/FormEdit.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Forms", url: "/forms" },
              { title: "Edit", active: true },
            ],
            pageTitle: "Forms",
            rule: "UserRoles",
            role: ["admin"],
            permission: "forms.update",
          },
        },
      ],
    },
    //=============================================================================
    // FULL PAGE LAYOUTS
    //=============================================================================
    {
      path: "",
      component: () => import("@/layouts/full-page/FullPage.vue"),
      children: [
        //=============================================================================
        // PAGES
        //=============================================================================
        {
          path: "/callback",
          name: "auth-callback",
          component: () => import("@/views/Callback.vue"),
        },
        {
          path: "/login",
          name: "page-login",
          component: () => import("@/views/pages/login/Login.vue"),
          meta: {
            rule: "UserRoles",
            role: ["*"],
            permission: null,
          },
        },
        {
          path: "/email/view/:id",
          name: "app-email-view",
          component: () =>
            import(
              "@/views/apps/template-email/template-view/TemplateView.vue"
            ),
          meta: {
            rule: "UserRoles",
            role: ["admin"],
            permission: null,
          },
        },
        {
          path: "/register",
          name: "page-register",
          component: () => import("@/views/pages/register/Register.vue"),
          meta: {
            rule: "UserRoles",
            role: ["*"],
            permission: null,
          },
        },
        {
          path: "/pages/forgot-password",
          name: "page-forgot-password",
          component: () => import("@/views/pages/ForgotPassword.vue"),
          meta: {
            rule: "UserRoles",
            role: ["*"],
            permission: null,
          },
        },
        {
          path: "/pages/reset-password",
          name: "page-reset-password",
          component: () => import("@/views/pages/ResetPassword.vue"),
          meta: {
            rule: "UserRoles",
            role: ["*"],
            permission: null,
          },
        },
        {
          path: "/pages/lock-screen",
          name: "page-lock-screen",
          component: () => import("@/views/pages/LockScreen.vue"),
          meta: {
            rule: "UserRoles",
            role: ["*"],
            permission: null,
          },
        },
        {
          path: "/pages/comingsoon",
          name: "page-coming-soon",
          component: () => import("@/views/pages/ComingSoon.vue"),
          meta: {
            rule: "UserRoles",
            role: ["*"],
            permission: null,
          },
        },
        {
          path: "/pages/error-404",
          name: "page-error-404",
          component: () => import("@/views/pages/Error404.vue"),
          meta: {
            rule: "UserRoles",
            role: ["*"],
            permission: null,
          },
        },
        {
          path: "/pages/error-500",
          name: "page-error-500",
          component: () => import("@/views/pages/Error500.vue"),
          meta: {
            rule: "UserRoles",
            role: ["*"],
            permission: null,
          },
        },
        {
          path: "/pages/not-authorized",
          name: "page-not-authorized",
          component: () => import("@/views/pages/NotAuthorized.vue"),
          meta: {
            rule: "UserRoles",
            role: ["*"],
            permission: null,
          },
        },
        {
          path: "/pages/maintenance",
          name: "page-maintenance",
          component: () => import("@/views/pages/Maintenance.vue"),
          meta: {
            rule: "UserRoles",
            role: ["*"],
            permission: null,
          },
        },
        {
          path: "/test-form/:formId/preview",
          name: "app-test-form-preview",
          component: () => import("@/views/apps/test-form/TestFormPreview.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Test Form", url: "/test-forms" },
              { title: "Preview", active: true },
            ],
            pageTitle: "Test Form Preview",
            rule: "UserRoles",
            role: ["admin"],
            permission: null,
          },
        },
        {
          path: "/lessons/:lessonId/pdf",
          name: "app-lesson-pdf-viewer",
          component: () =>
            import("@/views/apps/curriculum/LessonPdfViewer.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Curriculum", url: "/curriculum" },
              { title: "PdfViewer", active: true },
            ],
            pageTitle: "Lesson PdfViewer",
            rule: "UserRoles",
            role: ["admin", "teacher"],
            permission: "curriculums.index",
          },
        },
      ],
    },
    // Redirect to 404 page, if no match found
    {
      path: "*",
      redirect: "/pages/error-404",
    },
  ],
});

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById("loading-bg");
  if (appLoading) {
    appLoading.style.display = "none";
  }
});

// COMMENTED OUT: This guard causes race condition with the one in main.js
// The async onAuthStateChanged callback conflicts with localStorage-based auth check
router.beforeEach((to, from, next) => {
  firebase.auth().onAuthStateChanged(() => {
    // get firebase current user
    const firebaseCurrentUser = firebase.auth().currentUser;

    // If auth required, check login. If login fails redirect to login page
    if (to.meta.authRequired) {
      if (!(auth.isAuthenticated() || firebaseCurrentUser)) {
        router.push({ path: "/login", query: { to: to.path } });
      }
    }

    return next();
  });
});

export default router;
